import React from 'react';

const SectionTitle = ({ children }) => (
  <h3 className="text-lg font-semibold text-purple-700 mb-2">{children}</h3>
);

const Section = ({ children }) => (
  <div className="bg-white p-4 rounded-lg shadow-sm mb-4">{children}</div>
);

const SeasonalForecast = ({ seasonal }) => (
  <div className="grid grid-cols-2 gap-4 mt-4">
    {seasonal.map((forecast, index) => (
      <div key={index} className="bg-gray-50 p-3 rounded-lg">
        <span className="text-sm font-medium text-gray-600">
          {['봄', '여름', '가을', '겨울'][index]} 운세
        </span>
        <p className="text-gray-700 mt-1">{forecast}</p>
      </div>
    ))}
  </div>
);

const SajuResult = ({ result }) => {
  if (!result) return null;

  const { analysis, aspects, recommendations } = result;

  return (
    <div className="space-y-6 p-6 bg-gray-50 rounded-xl">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-purple-800">사주 분석 결과</h2>
        <p className="text-gray-600 mt-2">
          음양오행과 대운/세운을 기반으로 분석한 결과입니다
        </p>
      </div>

      <Section>
        <SectionTitle>종합 운세</SectionTitle>
        <p className="text-gray-700 whitespace-pre-line">{analysis.overall}</p>
        <div className="mt-4 p-3 bg-purple-50 rounded-lg">
          <h4 className="font-medium text-purple-700 mb-2">음양오행 분석</h4>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            {analysis.elements.map((element, index) => (
              <li key={index}>{element}</li>
            ))}
          </ul>
        </div>
      </Section>

      <Section>
        <SectionTitle>시기별 운세</SectionTitle>
        <div className="space-y-4">
          <p className="text-gray-700">{analysis.yearly}</p>
          <p className="text-gray-700">{analysis.monthly}</p>
          {analysis.seasonal && <SeasonalForecast seasonal={analysis.seasonal} />}
        </div>
      </Section>

      <Section>
        <SectionTitle>분야별 운세</SectionTitle>
        <div className="grid gap-4">
          {Object.entries(aspects).map(([key, value]) => (
            <div key={key} className={`border-l-4 border-${getColorByAspect(key)} pl-3`}>
              <h4 className={`font-medium text-${getColorByAspect(key)}`}>
                {getAspectTitle(key)}
              </h4>
              <p className="text-gray-700 mt-1">{value}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <SectionTitle>조언 및 개선방안</SectionTitle>
        <div className="bg-purple-50 p-4 rounded-lg space-y-4">
          <div>
            <h4 className="font-medium text-purple-700 mb-2">길방</h4>
            <p className="text-gray-700">{recommendations.lucky_directions.join(', ')}</p>
          </div>
          <div>
            <h4 className="font-medium text-purple-700 mb-2">행운의 색</h4>
            <p className="text-gray-700">{recommendations.lucky_colors.join(', ')}</p>
          </div>
          {recommendations.lucky_numbers && (
            <div>
              <h4 className="font-medium text-purple-700 mb-2">행운의 숫자</h4>
              <p className="text-gray-700">{recommendations.lucky_numbers.join(', ')}</p>
            </div>
          )}
          <div>
            <h4 className="font-medium text-purple-700 mb-2">주의사항</h4>
            <ul className="list-disc list-inside text-gray-700 space-y-1">
              {recommendations.cautions.map((caution, index) => (
                <li key={index}>{caution}</li>
              ))}
            </ul>
          </div>
          {recommendations.improvements && (
            <div>
              <h4 className="font-medium text-purple-700 mb-2">개선방안</h4>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                {recommendations.improvements.map((improvement, index) => (
                  <li key={index}>{improvement}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};

const getColorByAspect = (aspect) => {
  const colors = {
    career: 'purple-500',
    relationships: 'blue-500',
    wealth: 'green-500',
    health: 'red-500',
    love: 'pink-500',
    study: 'indigo-500'
  };
  return colors[aspect] || 'gray-500';
};

const getAspectTitle = (aspect) => {
  const titles = {
    career: '직업/사업운',
    relationships: '대인관계운',
    wealth: '재물운',
    health: '건강운',
    love: '애정운',
    study: '학업운'
  };
  return titles[aspect] || aspect;
};

export default SajuResult; 